import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  FaShoppingCart, 
  FaPlus, 
  FaMinus, 
  FaTrash,
  FaTimes,
  FaArrowRight,
  FaUtensils
} from 'react-icons/fa';
import { useCart } from '../../context/CartContext';
import { getCurrentLanguage } from '../../utils/languageUtils';
import './MiniCart.css';

// Cart rows are tiny: show the thumbnail when the cart item has one; if it is
// missing or fails, swap to the full-size image exactly once.
const fallbackToFullImage = (fullUrl) => (event) => {
  const el = event.currentTarget;
  if (fullUrl && el.getAttribute('src') !== fullUrl) {
    el.setAttribute('src', fullUrl);
  }
};

const MiniCart = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    items,
    totalItems,
    total,
    updateQuantity,
    removeItem,
    formatCurrency
  } = useCart();

  const currentLang = getCurrentLanguage(window.location.pathname);

  const handleViewCart = () => {
    navigate(`/${currentLang}/cart`);
    onClose();
  };

  const handleCheckout = () => {
    navigate(`/${currentLang}/checkout`);
    onClose();
  };

  const formatPrice = (price) => {
    return formatCurrency ? formatCurrency(price) : `₼${Number(price).toFixed(2)}`;
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="mini-cart-backdrop" onClick={onClose} />
      
      {/* Mini Cart */}
      <div className="mini-cart">
        {/* Header */}
        <div className="mini-cart-header">
          <h6 className="mb-0">
            <FaShoppingCart className="me-2" />
            {t('cart.title', 'Your Cart')} ({totalItems})
          </h6>
          <button 
            className="btn-close"
            onClick={onClose}
            aria-label="Close cart"
          >
            <FaTimes />
          </button>
        </div>

        {/* Content */}
        <div className="mini-cart-content">
          {items.length === 0 ? (
            <div className="mini-cart-empty">
              <FaUtensils className="empty-icon" />
              <p>{t('cart.empty', 'Your cart is empty')}</p>
            </div>
          ) : (
            <>
              {/* Items */}
              <div className="mini-cart-items">
                {items.slice(0, 3).map((item, index) => (
                  <div key={`${item.id}-${index}`} className="mini-cart-item">
                    <div className="item-image">
                      {item.image ? (
                        <img
                          src={item.image_thumb || item.image}
                          alt={item.name}
                          loading="lazy"
                          decoding="async"
                          onError={fallbackToFullImage(item.image)}
                        />
                      ) : (
                        <div className="placeholder-image">
                          <FaUtensils />
                        </div>
                      )}
                    </div>
                    
                    <div className="item-details">
                      <h6>{item.name}</h6>
                      <p className="price">{formatPrice(item.price)}</p>
                      
                      <div className="quantity-controls">
                        <button
                          className="btn-quantity"
                          onClick={() => updateQuantity(index, item.quantity - 1)}
                          disabled={item.quantity <= 1}
                        >
                          <FaMinus />
                        </button>
                        <span>{item.quantity}</span>
                        <button
                          className="btn-quantity"
                          onClick={() => updateQuantity(index, item.quantity + 1)}
                        >
                          <FaPlus />
                        </button>
                      </div>
                    </div>

                    <div className="item-actions">
                      <button
                        className="btn-remove"
                        onClick={() => removeItem(index)}
                        title={t('cart.remove', 'Remove')}
                      >
                        <FaTrash />
                      </button>
                      <div className="item-total">
                        {formatPrice(item.total)}
                      </div>
                    </div>
                  </div>
                ))}

                {items.length > 3 && (
                  <div className="more-items">
                    <p>+{items.length - 3} {t('cart.moreItems', 'more items')}</p>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="mini-cart-footer">
                <div className="total-section">
                  <div className="total-row">
                    <span>{t('cart.total', 'Total')}</span>
                    <span className="total-amount">{formatPrice(total)}</span>
                  </div>
                </div>

                <div className="actions">
                  <button 
                    className="btn btn-outline-primary"
                    onClick={handleViewCart}
                  >
                    {t('cart.viewCart', 'View Cart')}
                  </button>
                  <button 
                    className="btn btn-primary"
                    onClick={handleCheckout}
                  >
                    {t('cart.checkout', 'Checkout')}
                    <FaArrowRight className="ms-2" />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default MiniCart;
